import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@supabase/supabase-js";

/**
 * Auth-readiness singleton.
 *
 * The Supabase client restores the session from localStorage asynchronously
 * after `createClient()` returns. Any RLS-protected query that fires before
 * that restore completes is sent with an anonymous JWT and silently returns
 * zero rows — surfaced to the user as "world not found" / "profile not
 * found" until they hit reload.
 *
 * `whenAuthReady()` resolves once we've heard back from `getSession()` AT
 * LEAST once, so every gated query is guaranteed to ride a fully-hydrated
 * client. Resolves with the *current* session at that moment (which may be
 * `null` if the user is signed out).
 *
 * The hook variant exposes the same readiness to React components so they
 * can `enabled: isReady && !!user` their data-fetching effects.
 */

let resolveReady: (s: Session | null) => void;
const readyPromise: Promise<Session | null> = new Promise((resolve) => {
  resolveReady = resolve;
});
let readyResolved = false;
let cachedSession: Session | null = null;

// Kick off the one-shot restore as soon as this module is imported.
supabase.auth
  .getSession()
  .then(({ data }) => {
    cachedSession = data.session ?? null;
    if (!readyResolved) {
      readyResolved = true;
      resolveReady(cachedSession);
    }
  })
  .catch(() => {
    if (!readyResolved) {
      readyResolved = true;
      resolveReady(null);
    }
  });

// Keep the cached session in sync so synchronous reads (after readiness)
// reflect the latest signed-in / signed-out state.
supabase.auth.onAuthStateChange((_event, session) => {
  cachedSession = session ?? null;
  if (!readyResolved) {
    readyResolved = true;
    resolveReady(cachedSession);
  }
  // Notify any React subscribers.
  listeners.forEach((fn) => {
    try {
      fn(cachedSession);
    } catch {
      /* listener errors must not break auth flow */
    }
  });
});

export function whenAuthReady(): Promise<Session | null> {
  return readyPromise;
}

export function isAuthReadyNow(): boolean {
  return readyResolved;
}

export function getCachedSession(): Session | null {
  return cachedSession;
}

export function getCachedUser(): User | null {
  return cachedSession?.user ?? null;
}

// ── React subscription plumbing ──────────────────────────────────────────────

type Listener = (s: Session | null) => void;
const listeners = new Set<Listener>();

export function subscribeAuth(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}